import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from 'components/ui/Modal';
import LoginForm from 'components/auth/LoginForm';
import { ROUTES } from 'config';

interface LoginModalContextType {
  openLoginModal: (onSuccess?: () => void) => void;
  closeLoginModal: () => void;
}

const LoginModalContext = createContext<LoginModalContextType | undefined>(undefined);

export const useLoginModal = () => {
  const context = useContext(LoginModalContext);
  if (context === undefined) {
    throw new Error('useLoginModal must be used within a LoginModalProvider');
  }
  return context;
};

interface LoginModalProviderProps {
  children: ReactNode;
}

export const LoginModalProvider: React.FC<LoginModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>(undefined);
  const location = useLocation();

  const openLoginModal = useCallback((onSuccess?: () => void) => {
    setOnSuccessCallback(() => onSuccess);
    setIsOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsOpen(false);
    setOnSuccessCallback(undefined);
  }, []);

  const handleLoginSuccess = useCallback(() => {
    closeLoginModal();
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  }, [onSuccessCallback, closeLoginModal]);

  // Close modal when navigating to register or forgot password page
  useEffect(() => {
    if (isOpen && (location.pathname === ROUTES.REGISTER || location.pathname === ROUTES.FORGOT_PASSWORD)) {
      closeLoginModal();
    }
  }, [location.pathname, isOpen, closeLoginModal]);

  return (
    <LoginModalContext.Provider value={{ openLoginModal, closeLoginModal }}>
      {children}
      <Modal
        isOpen={isOpen}
        onClose={closeLoginModal}
        size="md"
      >
        <LoginForm
          isModal={true}
          onSuccess={handleLoginSuccess}
          onCancel={closeLoginModal}
        />
      </Modal>
    </LoginModalContext.Provider>
  );
};

